import React, { useEffect, useState, memo, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ChevronDown, ArrowRight, Book, Crosshair, HeartPulse, DoorOpen, Map, XIcon } from 'lucide-react';
// --- Meta Pixel helpers ---
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
function initMetaPixel() {
  if (window.fbq) return;
  const n: any = window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(t);
  window.fbq('init', '776524068662312');
  window.fbq('track', 'PageView');
}
// --- Shopify URL (replace with your actual checkout link) ---
const SHOPIFY_URL = 'https://www.nachogerberg.com/cart/42742166224967:1';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-2">
    {children}
  </motion.div>;
const CTAButton = ({
  className = '',
  scrollTo
}: {
  className?: string;
  scrollTo?: string;
}) => {
  const handleClick = () => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth'
        });
        return;
      }
    }
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'El Kit de Supervivencia del Small Talk',
        content_type: 'product',
        value: 17.0,
        currency: 'USD'
      });
    }
    window.open(SHOPIFY_URL, '_blank');
  };
  return <button onClick={handleClick} className={`bg-[#f5c518] hover:bg-yellow-400 text-[#0f1b2d] font-bold py-4 px-8 rounded-lg text-lg md:text-xl transition-transform hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2 w-full md:w-auto cursor-pointer ${className}`} data-id="element-3">
      DESCARGAR EL KIT AHORA
      <ArrowRight className="w-6 h-6" data-id="element-4" />
    </button>;
};
// --- Legal Modal ---
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-5">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-id="element-6">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-7">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-8">
              <h2 className="text-xl font-bold text-[#0f1b2d]" data-id="element-9">{title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-10">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-11" />
              </button>
            </div>
            <div className="px-6 py-6 text-gray-700 text-sm leading-relaxed space-y-4" data-id="element-12">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// --- Footer ---
function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="bg-[#0a1320] text-gray-400 py-8 px-6 border-t border-white/5" data-id="element-13">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-14">
          <p className="text-sm" data-id="element-15">
            © {new Date().getFullYear()} El Kit de Supervivencia del Small
            Talk. Todos los derechos reservados.
          </p>
          <div className="flex gap-6" data-id="element-16">
            <button onClick={() => setShowPrivacy(true)} className="text-sm hover:text-white transition-colors underline underline-offset-4 cursor-pointer" data-id="element-17">
              Política de Privacidad
            </button>
            <button onClick={() => setShowTerms(true)} className="text-sm hover:text-white transition-colors underline underline-offset-4 cursor-pointer" data-id="element-18">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" data-id="element-19">
        <p data-id="element-20">
          <strong data-id="element-21">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-22">
          Tu privacidad es importante para nosotros. Esta política describe cómo
          recopilamos, usamos y protegemos tu información personal cuando
          visitas esta página o realizas una compra.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-23">
          Información que recopilamos
        </h3>
        <p data-id="element-24">
          Recopilamos información que nos proporcionas directamente al realizar
          una compra, como tu nombre, dirección de correo electrónico e
          información de pago. También recopilamos datos de uso automáticamente
          mediante cookies y tecnologías similares (como el Meta Pixel) para
          mejorar nuestros servicios y publicidad.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-25">
          Uso de la información
        </h3>
        <p data-id="element-26">
          Utilizamos tu información para procesar transacciones, entregar el
          producto digital adquirido, mejorar nuestra página y personalizar tu
          experiencia. No vendemos ni compartimos tu información personal con
          terceros, excepto lo necesario para procesar pagos y cumplir con la
          ley.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-27">
          Cookies y seguimiento
        </h3>
        <p data-id="element-28">
          Utilizamos el Meta Pixel de Facebook para medir la efectividad de
          nuestra publicidad y ofrecerte anuncios relevantes. Puedes gestionar
          tus preferencias de cookies en la configuración de tu navegador.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-29">
          Tus derechos
        </h3>
        <p data-id="element-30">
          Tienes derecho a acceder, rectificar o eliminar tu información
          personal. Para ejercer estos derechos, puedes contactarnos a través de
          los canales disponibles en nuestra tienda.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-31">Seguridad</h3>
        <p data-id="element-32">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o destrucción.
        </p>
      </LegalModal>

      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" data-id="element-33">
        <p data-id="element-34">
          <strong data-id="element-35">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-36">
          Al acceder a esta página y/o realizar una compra, aceptas los
          siguientes términos y condiciones. Por favor, léelos cuidadosamente.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-37">
          Producto digital
        </h3>
        <p data-id="element-38">
          "El Kit de Supervivencia del Small Talk" es un producto digital en
          formato PDF. Una vez completada la compra, recibirás acceso
          instantáneo al material. Debido a la naturaleza digital del producto,
          no se aceptan devoluciones una vez descargado el contenido.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-39">
          Uso personal
        </h3>
        <p data-id="element-40">
          El contenido adquirido es para uso personal y no comercial. Queda
          prohibida la reproducción, distribución, modificación o reventa del
          material sin autorización expresa por escrito.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-41">
          Propiedad intelectual
        </h3>
        <p data-id="element-42">
          Todo el contenido, incluyendo textos, gráficos, diseños y metodologías
          presentadas, está protegido por derechos de autor. El uso no
          autorizado puede resultar en acciones legales.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-43">
          Limitación de responsabilidad
        </h3>
        <p data-id="element-44">
          Los resultados pueden variar según la persona. No garantizamos
          resultados específicos derivados del uso del material. El contenido es
          educativo e informativo y no sustituye asesoramiento profesional.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-45">Pagos</h3>
        <p data-id="element-46">
          Todos los pagos se procesan de forma segura a través de Shopify. No
          almacenamos información de tarjetas de crédito en nuestros servidores.
        </p>
        <h3 className="font-bold text-[#0f1b2d] text-base mt-4" data-id="element-47">
          Modificaciones
        </h3>
        <p data-id="element-48">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Los cambios entrarán en vigor inmediatamente después de su
          publicación en esta página.
        </p>
      </LegalModal>
    </>;
}
export function LandingPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen font-sans selection:bg-[#f5c518] selection:text-[#0f1b2d]" data-id="element-49">
      {/* 1. HERO SECTION */}
      <section className="bg-[#0f1b2d] text-white pt-12 pb-14 px-6 relative overflow-hidden" data-id="element-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 items-center" data-id="element-51">
          <FadeIn data-id="element-52">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 font-heading text-center" data-id="element-53">
              <span className="text-[#f5c518]" data-id="element-54">
                El Kit de Supervivencia del Small Talk
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed" data-id="element-55">
              El sistema mecánico de 5 pasos para no quedarte nunca más sin nada
              que decir, incluso si hoy te consideras la persona más tímida del
              mundo.
            </p>
            <CTAButton scrollTo="precio" data-id="element-56" />
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-57">
            <div className="relative mx-auto max-w-xs" data-id="element-58">
              <div className="absolute inset-0 bg-[#f5c518] blur-3xl opacity-20 rounded-full" data-id="element-59"></div>
              <img src="https://cdn.magicpatterns.com/uploads/477SRRTGMoF5fkNaiFdz4b/portada.png" alt="El Kit de Supervivencia del Small Talk" className="relative z-10 w-full h-auto drop-shadow-2xl rounded-lg" data-id="element-60" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="bg-white text-[#0f1b2d] py-14 px-6" data-id="element-61">
        <div className="max-w-3xl mx-auto text-center" data-id="element-62">
          <FadeIn data-id="element-63">
            <p className="text-2xl md:text-3xl font-medium leading-relaxed mb-5" data-id="element-64">
              Estás ahí. En una primera cita, en el ascensor con tu jefe o en
              una boda donde no conoces a nadie. La charla inicial se agota. El
              aire se vuelve pesado. Miras tu teléfono desesperadamente buscando
              una notificación que no existe.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-65">
              Ese "agujero negro" social no solo es incómodo; está matando tus
              oportunidades de negocio, tus conexiones románticas y tu confianza
              de forma silenciosa cada vez que ocurre.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="bg-[#0f1b2d] text-white py-14 px-6" data-id="element-66">
        <div className="max-w-4xl mx-auto" data-id="element-67">
          <FadeIn data-id="element-68">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 font-heading" data-id="element-69">
              El problema no es tu personalidad
            </h2>
            <div className="bg-white/5 p-6 md:p-10 rounded-2xl border border-white/10" data-id="element-70">
              <p className="text-xl mb-4 leading-relaxed" data-id="element-71">
                La mayoría de la gente cree que la "fluidez social" es un don
                místico con el que se nace. O eres carismático, o eres el que se
                queda pegado a la pared.
              </p>
              <p className="text-xl leading-relaxed" data-id="element-72">
                Tratas de memorizar chistes, buscas "preguntas profundas" en
                Google o, peor aún, te obligas a "ser tú mismo" mientras tu
                cerebro grita que salgas corriendo. El problema no es tu
                personalidad.{' '}
                <span className="text-[#f5c518] font-bold" data-id="element-73">
                  El problema es que estás intentando conducir sin motor.
                </span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="bg-gray-50 text-[#0f1b2d] py-14 px-6" data-id="element-74">
        <div className="max-w-5xl mx-auto" data-id="element-75">
          <FadeIn data-id="element-76">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 font-heading" data-id="element-77">
              Por qué lo que has intentado no funciona
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-78">
              {['Las preguntas abiertas a veces parecen un interrogatorio policial y ponen a la otra persona a la defensiva.', 'Memorizar guiones falla en cuanto la otra persona dice algo que no esperabas.', 'Esperar a que la otra persona lleve el peso de la conversación te hace parecer desinteresado o aburrido.', "Beber alcohol para 'soltarse' solo enmascara el problema y no te da habilidades reales."].map((text, i) => <div key={i} className="flex gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100" data-id="element-79">
                  <XCircle className="w-8 h-8 text-red-500 flex-shrink-0" data-id="element-80" />
                  <p className="text-lg text-gray-700" data-id="element-81">{text}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM */}
      <section className="bg-[#0f1b2d] text-white py-14 px-6 border-t-8 border-[#f5c518]" data-id="element-82">
        <div className="max-w-4xl mx-auto text-center" data-id="element-83">
          <FadeIn data-id="element-84">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 font-heading text-[#f5c518]" data-id="element-85">
              "Conversation Threading" <br className="hidden md:block" data-id="element-86" />
              <span className="text-white text-3xl md:text-4xl" data-id="element-87">
                (Hilado de Conversación)
              </span>
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed mb-5" data-id="element-88">
              Este no es un libro de autoayuda; es un manual de ingeniería
              social.
            </p>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed bg-white/5 p-6 rounded-xl" data-id="element-89">
              El Hilado de Conversación es la habilidad mecánica de detectar
              "hilos" o ganchos en cualquier frase que alguien te diga. Una vez
              que aprendes a verlos, es físicamente imposible quedarte sin
              temas.{' '}
              <strong className="text-white" data-id="element-90">
                Pasas de la reacción al control absoluto de la charla en menos
                de 3 segundos.
              </strong>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="bg-white text-[#0f1b2d] py-14 px-6" data-id="element-91">
        <div className="max-w-5xl mx-auto" data-id="element-92">
          <FadeIn data-id="element-93">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 font-heading" data-id="element-94">
              Lo que te llevas hoy
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-95">
              {[{
              title: 'El Manual Principal',
              desc: 'El Kit de Supervivencia del Small Talk (PDF).',
              icon: Book
            }, {
              title: 'La Matriz de Detección',
              desc: 'Para encontrar 3 rutas de escape en cada frase.',
              icon: Crosshair
            }, {
              title: 'El Kit de Reanimación',
              desc: "5 frases de 'emergencia' para resucitar una charla muerta.",
              icon: HeartPulse
            }, {
              title: 'Guía de Salidas Elegantes',
              desc: 'Cómo irte de una conversación sin parecer un maleducado.',
              icon: DoorOpen
            }, {
              title: 'Radar de Información',
              desc: 'Cómo usar el entorno para no depender de tu memoria.',
              icon: Map
            }].map((item, i) => <div key={i} className="bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow" data-id="element-96">
                  <div className="bg-[#0f1b2d] w-12 h-12 rounded-full flex items-center justify-center mb-4" data-id="element-97">
                    <item.icon className="w-6 h-6 text-[#f5c518]" data-id="element-98" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-99">{item.title}</h3>
                  <p className="text-gray-600" data-id="element-100">{item.desc}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="bg-[#0f1b2d] text-white py-14 px-6" data-id="element-101">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-102">
          <FadeIn data-id="element-103">
            <div className="bg-white/5 p-6 md:p-8 rounded-2xl border border-white/10 h-full" data-id="element-104">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3" data-id="element-105">
                <CheckCircle2 className="w-8 h-8 text-green-400" data-id="element-106" />
                Esto ES para ti si...
              </h3>
              <ul className="space-y-4" data-id="element-107">
                {['Introvertidos que quieren navegar eventos sociales sin ansiedad.', 'Profesionales que pierden oportunidades por no saber hacer networking casual.', 'Solteros que temen el silencio incómodo en las primeras citas.', "Personas que sienten que 'no son interesantes' y no saben cómo aportar."].map((text, i) => <li key={i} className="flex gap-4" data-id="element-108">
                    <div className="w-2 h-2 bg-green-400 rounded-full mt-2.5 flex-shrink-0" data-id="element-109" />
                    <span className="text-lg text-gray-300" data-id="element-110">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-111">
            <div className="bg-white/5 p-6 md:p-8 rounded-2xl border border-white/10 h-full" data-id="element-112">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3" data-id="element-113">
                <XCircle className="w-8 h-8 text-red-400" data-id="element-114" />
                Esto NO es para ti si...
              </h3>
              <ul className="space-y-4" data-id="element-115">
                {["Personas que buscan tácticas de manipulación o 'Pick Up Artist'.", 'Extrovertidos naturales que ya dominan cualquier sala.', 'Quienes no estén dispuestos a practicar una técnica mecánica simple.'].map((text, i) => <li key={i} className="flex gap-4" data-id="element-116">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2.5 flex-shrink-0" data-id="element-117" />
                    <span className="text-lg text-gray-300" data-id="element-118">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="bg-gray-50 text-[#0f1b2d] py-14 px-6" data-id="element-119">
        <div className="max-w-4xl mx-auto" data-id="element-120">
          <FadeIn data-id="element-121">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 font-heading" data-id="element-122">
              Cómo funciona el sistema
            </h2>
            <div className="space-y-5" data-id="element-123">
              {[{
              step: '1',
              title: "Identificas los 'hilos'",
              desc: 'En lo que la otra persona acaba de decir.'
            }, {
              step: '2',
              title: 'Eliges un hilo',
              desc: 'Basado en tu objetivo (conectar o curiosear).'
            }, {
              step: '3',
              title: 'Tiras del hilo',
              desc: 'Usando la fórmula de Observación + Opinión.'
            }].map((item, i) => <div key={i} className="flex items-center gap-5 bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-124">
                  <div className="w-14 h-14 bg-[#f5c518] text-[#0f1b2d] rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0" data-id="element-125">
                    {item.step}
                  </div>
                  <div data-id="element-126">
                    <h3 className="text-xl md:text-2xl font-bold mb-1" data-id="element-127">
                      {item.title}
                    </h3>
                    <p className="text-lg text-gray-600" data-id="element-128">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 9. PROOF */}
      <section className="bg-[#0f1b2d] text-white py-14 px-6 text-center" data-id="element-129">
        <div className="max-w-3xl mx-auto" data-id="element-130">
          <FadeIn data-id="element-131">
            <h2 className="text-3xl md:text-4xl font-bold mb-5 font-heading" data-id="element-132">
              Resultados en 7 días
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed text-gray-300 mb-5" data-id="element-133">
              Pasarás de evitar visualmente a la gente en el pasillo a ser la
              persona que inicia y sostiene interacciones breves y fluidas sin
              que se te acelere el corazón.
            </p>
            <p className="text-2xl font-bold text-[#f5c518]" data-id="element-134">
              No es magia, es estructura.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="bg-white text-[#0f1b2d] py-14 px-6" data-id="element-135">
        <div className="max-w-3xl mx-auto" data-id="element-136">
          <FadeIn data-id="element-137">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 font-heading" data-id="element-138">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-3" data-id="element-139">
              {[{
              q: '¿Esto sirve para entornos profesionales?',
              a: "Sí, especialmente para el 'coffee break' o antes de que empiece una reunión."
            }, {
              q: '¿Tengo que memorizar mucho?',
              a: 'No, la técnica se basa en lo que la OTRA persona dice, no en lo que tú traigas preparado.'
            }, {
              q: '¿Es un libro muy largo?',
              a: 'No, es un kit táctico diseñado para leerse en una tarde y aplicarse esa misma noche.'
            }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-140" />)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 11. FINAL CTA / PRICE BLOCK */}
      <section id="precio" className="bg-[#0f1b2d] text-white py-14 px-6 border-t border-white/10" data-id="element-141">
        <div className="max-w-4xl mx-auto text-center" data-id="element-142">
          <FadeIn data-id="element-143">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-heading" data-id="element-144">
              Elimina el miedo al vacío social hoy mismo
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-145">
              Obtén el sistema completo que te dará seguridad de por vida por
              menos de lo que cuesta una cena que probablemente te ahorrarías
              por timidez.
            </p>

            <div className="bg-white/5 border border-[#f5c518]/30 rounded-3xl p-6 md:p-10 max-w-xl mx-auto mb-6" data-id="element-146">
              <p className="text-gray-400 text-lg mb-2 uppercase tracking-wider font-bold" data-id="element-147">
                Inversión única
              </p>
              <p className="text-2xl text-gray-400 line-through mb-2" data-id="element-148">$17</p>
              <p className="text-6xl font-extrabold text-[#f5c518] mb-1" data-id="element-149">
                $6.39
              </p>
              <p className="text-sm text-[#f5c518] font-semibold uppercase tracking-wider mb-6" data-id="element-150">
                Solo por hoy
              </p>
              <CTAButton className="w-full text-xl py-5" data-id="element-151" />
              <p className="mt-4 text-sm text-gray-400 flex items-center justify-center gap-2" data-id="element-152">
                <CheckCircle2 className="w-4 h-4" data-id="element-153" />
                Acceso instantáneo. Formato PDF compatible con todos los
                dispositivos.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <Footer data-id="element-154" />
    </div>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-155">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-6 text-left bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-156">
        <span className="font-bold text-lg" data-id="element-157">{question}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-158" />
      </button>
      <AnimatePresence data-id="element-159">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-160">
            <div className="p-6 bg-white text-gray-600 border-t border-gray-200" data-id="element-161">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}